import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { messagesService } from '../services/messagesService';
import { supabase } from '../lib/supabase';
import QuickMessageTemplates from '../components/QuickMessageTemplates';

export default function Chat() {
  const { id: conversationId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [listing, setListing] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showQuickTemplates, setShowQuickTemplates] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadConversation();
    loadMessages();
  }, [conversationId, user, navigate]);

  // Detect screen size for responsive chat view
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (conversationId) {
      const unsubscribe = subscribeToMessages();
      
      // Fallback: Poll for new messages every 2 seconds
      const pollInterval = setInterval(() => {
        loadMessages(true);
      }, 2000);
      
      return () => {
        unsubscribe();
        clearInterval(pollInterval);
      };
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // Scroll to bottom with some padding to ensure messages are visible above the input
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }
  };

  const markMessagesAsRead = async () => {
    try {
      console.log('📖 Marking messages as read for conversation:', conversationId);
      const { data, error } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error fetching unread messages:', error);
        return;
      }

      if (data && data.length > 0) {
        console.log('📖 Found unread messages to mark:', data.length);
        await messagesService.markAsRead(data.map(msg => msg.id));
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  const loadConversation = async () => {
    try {
      console.log('Loading conversation:', conversationId);
      
      // Load conversation
      const { data: convData, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      console.log('Conversation response:', { convData, convError });
      
      if (convError) {
        console.error('Supabase conversation error:', convError);
        throw convError;
      }
      
      if (!convData) {
        throw new Error('Conversation not found');
      }
      
      setConversation(convData);

      // Load listing
      const { data: listingData, error: listingError } = await supabase
        .from('listings')
        .select('*')
        .eq('id', convData.listing_id)
        .single();

      if (listingError) {
        console.error('Error loading listing:', listingError);
      } else {
        setListing(listingData);
      }

      // Load other user profile
      const otherUserId = user.id === convData.buyer_id ? convData.seller_id : convData.buyer_id;
      console.log('Loading other user profile for ID:', otherUserId);
      
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', otherUserId)
        .single();

      console.log('User profile response:', { userData, userError });
      
      if (userError && userError.code === 'PGRST116') {
        console.log('Profile does not exist, creating fallback user');
        // Profile doesn't exist - create a fallback
        setOtherUser({
          id: otherUserId,
          name: `User ${otherUserId.slice(0, 8)}`,
          email: 'user@example.com'
        });
      } else if (userError) {
        console.error('Error loading user profile:', userError);
        setOtherUser({
          id: otherUserId,
          name: `User ${otherUserId.slice(0, 8)}`,
          email: 'user@example.com'
        });
      } else {
        setOtherUser(userData || {
          id: otherUserId,
          name: `User ${otherUserId.slice(0, 8)}`,
          email: 'user@example.com'
        });
      }

    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(`Failed to load conversation: ${err.message}`);
    }
  };

  const loadMessages = async (isPolling = false) => {
    try {
      if (!isPolling) {
        setLoading(true);
        setError('');
      }
      
      if (!isPolling) {
        console.log('Loading messages for conversation:', conversationId);
      }
      
      const data = await messagesService.getMessages(conversationId);
      
      if (!isPolling) {
        console.log('Messages loaded:', data);
      }
      
      // Only update if messages actually changed
      setMessages(prevMessages => {
        if (JSON.stringify(prevMessages) === JSON.stringify(data)) {
          return prevMessages; // No change
        }
        if (!isPolling) {
          console.log('Messages updated');
        }
        return data;
      });

      // Mark messages as read
      const unreadMessages = data.filter(msg => !msg.is_read && msg.recipient_id === user.id);
      if (unreadMessages.length > 0) {
        console.log('Marking messages as read:', unreadMessages);
        await messagesService.markAsRead(unreadMessages.map(msg => msg.id));
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      if (!isPolling) {
        setError('Failed to load messages.');
      }
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  };

  const subscribeToMessages = () => {
    console.log('Setting up message subscription for conversation:', conversationId);
    
    const subscription = messagesService.subscribeToMessages(conversationId, (payload) => {
      console.log('New message received:', payload);
      
      if (payload.eventType === 'INSERT') {
        console.log('Adding new message to state:', payload.new);
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev.some(msg => msg.id === payload.new.id);
          if (exists) {
            console.log('Message already exists, not adding duplicate');
            return prev;
          }
          return [...prev, payload.new];
        });
        
        // Mark as read if it's for current user
        if (payload.new.recipient_id === user.id) {
          console.log('Marking message as read');
          messagesService.markAsRead([payload.new.id]);
        }
      }
    });

    return () => {
      console.log('Unsubscribing from messages');
      subscription.unsubscribe();
    };
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      
      const otherUserId = user.id === conversation.buyer_id ? conversation.seller_id : conversation.buyer_id;
      
      console.log('Sending message:', {
        conversationId,
        senderId: user.id,
        recipientId: otherUserId,
        content: newMessage.trim()
      });
      
      const result = await messagesService.sendMessage(
        conversationId,
        user.id,
        otherUserId,
        newMessage.trim()
      );

      console.log('Message sent successfully:', result);
      setNewMessage('');
      
      // Mark messages as read when user sends a message (they're actively in the conversation)
      setTimeout(() => {
        markMessagesAsRead();
      }, 500);
      
    } catch (err) {
      console.error('Error sending message:', err);
      setError(`Failed to send message: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = async (templateText) => {
    if (!templateText.trim() || sending || !conversation) return;
    
    setSending(true);
    setError('');
    
    try {
      const otherUserId = user.id === conversation.buyer_id ? conversation.seller_id : conversation.buyer_id;
      
      await messagesService.sendMessage(
        conversationId, 
        user.id, 
        otherUserId, 
        templateText.trim()
      );
      setNewMessage(''); // Clear the input
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // getOtherUser is now handled by state

  if (loading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-slate-200 rounded-full loading-skeleton"></div>
            <div className="flex-1">
              <div className="h-4 bg-slate-200 rounded loading-skeleton w-32 mb-1"></div>
              <div className="h-3 bg-slate-200 rounded loading-skeleton w-24"></div>
            </div>
          </div>
        </div>
        <div className="flex-1 p-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-xs">
                <div className="h-8 bg-slate-200 rounded-lg loading-skeleton"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !conversation) {
    return (
      <div className="container-center mx-auto max-w-2xl py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate('/messages')}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Back to Messages
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-container ${isMobile ? 'fullscreen' : 'desktop'} bg-white overflow-hidden w-full`}>
      {/* Header */}
      <div className="flex-shrink-0 bg-white/90 backdrop-blur-sm border-b border-slate-200 px-4 py-3 shadow-sm">
        <div className="flex items-center space-x-3">
          <Link
            to="/messages"
            className="p-1 hover:bg-slate-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          
          <button 
            onClick={() => navigate(`/profile/${otherUser?.id}`)}
            className="flex-shrink-0 hover:scale-105 transition-transform duration-200"
            title={`View ${otherUser?.name || 'User'}'s profile`}
          >
            {otherUser?.avatar_url ? (
              <img 
                src={otherUser.avatar_url} 
                alt={otherUser.name || 'User Avatar'} 
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md" 
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold border-2 border-white shadow-md">
                {otherUser?.name ? otherUser.name.charAt(0).toUpperCase() : '?'}
              </div>
            )}
          </button>
          
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">{otherUser?.name || 'Unknown User'}</h2>
            {listing && (
              <p className="text-sm text-slate-600 truncate">
                About: {listing.title}
              </p>
            )}
          </div>
          
          {/* Mark as Read Button */}
          <button
            onClick={markMessagesAsRead}
            className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-full font-medium transition-colors"
            title="Mark messages as read"
          >
            Mark Read
          </button>

          {listing && (
            <Link
              to={`/listing/${listing.id}`}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>
          )}
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="chat-messages p-4 space-y-3 bg-gradient-to-b from-slate-50 to-blue-50"
      >
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">💬</div>
            <p className="text-slate-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.sender_id === user.id;
            return (
              <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-2' : 'order-1'}`}>
                  <div className={`rounded-2xl px-4 py-2 ${
                    isOwn 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                      : 'bg-white text-slate-900 shadow-sm'
                  }`}>
                    <p className="text-sm">{message.content}</p>
                  </div>
                  <p className={`text-xs text-slate-500 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                    {formatTime(message.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} className="pb-4" />
      </div>

      {/* Message Input */}
      <div className="chat-input bg-white/90 backdrop-blur-md p-3 shadow-lg">
        {/* Quick Templates Button */}
        <div className="mb-2">
          <button
            onClick={() => setShowQuickTemplates(true)}
            className="flex items-center gap-2 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Quick Messages
          </button>
        </div>
        
        <form onSubmit={sendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 form-input py-2"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </form>
        
        {error && (
          <div className="mt-1 text-red-600 text-xs">{error}</div>
        )}
      </div>
      
      {/* Quick Message Templates */}
      <QuickMessageTemplates
        onSelectTemplate={handleTemplateSelect}
        isVisible={showQuickTemplates}
        onClose={() => setShowQuickTemplates(false)}
      />
    </div>
  );
}
